import React, { useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance, { API_BASE_URL } from '../utils/axiosConfig';
import { BolumEtiketi, BolumDetayKarti, PaylasimRenkSkalasi, paylasimRengi, toplamPaylasimOrani } from './BolumPaylasimDetay';

/**
 * Kat Karşılığı → Bölümler → "Görünüm" modu. Bloklar & Katlar → Görünümler'de oluşturulan görünümler
 * seçilir; her bölüm, paylaşım oranına göre renklenen ad etiketiyle gösterilir. Üzerine gelince ya da
 * tıklayınca (sabitlenir) bilgileri ve paylaşımları açılır. Fare tekerleğiyle yakınlaşılır, yakınken
 * sürüklenerek kaydırılır. Yan paneldeki ortak/müteahhit bir bölüme bırakılınca paylaşım açılır.
 */

export interface GorunumBolum {
    bolumID: number;
    bolumAdi: string;
    bolumTipi: string | null;
    m2: number;
    fiyat: number;
    paylasimlar: { paylasimID: number; ad: string; tip: 'O' | 'M'; oranYuzde: number }[];
}

interface Yerlesim { daireID: number; cepheID?: number | null; xYuzde: number; yYuzde: number; rxMin?: number | null; ryMin?: number | null; rxMax?: number | null; ryMax?: number | null; }
interface Cephe { cepheID: number; cepheAdi: string; gorselYolu?: string; siraNo: number; }
interface Gorunum { gorunumID: number; gorunumAdi: string; gorunumTipi: string; arkaplanGorsel?: string | null; daireler: Yerlesim[]; cepheler: Cephe[]; }

interface Props {
    arsaId: number;
    bolumler: GorunumBolum[];
    /** Bir kişi sürükleniyor mu — bölümler bırakma hedefi olarak vurgulanır. */
    surukleniyor: boolean;
    /** Sürüklenen kişi bu bölüme bırakılamaz mı (dolu ya da bütçe dışı). */
    birakilamaz: (bolumID: number) => boolean;
    onBirak: (bolumID: number) => void;
    onPaylasimEkle: (bolumID: number) => void;
}

const MAX_ZOOM = 8;

const KatKarsiligGorunumPaylasim: React.FC<Props> = ({ arsaId, bolumler, surukleniyor, birakilamaz, onBirak, onPaylasimEkle }) => {
    const [gorunumler, setGorunumler] = useState<Gorunum[] | null>(null);
    const [hata, setHata] = useState(false);
    const [seciliID, setSeciliID] = useState<number | null>(null);
    const [cepheID, setCepheID] = useState<number | null>(null);
    const [ustteki, setUstteki] = useState<number | null>(null);
    const [sabit, setSabit] = useState<number | null>(null);
    const [hedef, setHedef] = useState<number | null>(null);
    // Yakınlaştırma: tuval pikselinde kaydırma (tx, ty) + ölçek (z); içerik her zaman alanı doldurur
    const [gorunum, setGorunum] = useState({ z: 1, tx: 0, ty: 0 });
    const alanRef = useRef<HTMLDivElement>(null);
    const kaydirRef = useRef<{ x: number; y: number; tx: number; ty: number; hareket: boolean } | null>(null);
    // mouseup click'ten önce gelir: kaydırma sürüklemesi bittiğinde gelen tıklamayı yok saymak için
    const sonSurukleme = useRef(false);

    useEffect(() => {
        axiosInstance.get(`/api/KatKarsiligGorunumleri?arsaID=${arsaId}`)
            .then(r => {
                const liste: Gorunum[] = r.data ?? [];
                setGorunumler(liste);
                if (liste.length > 0) setSeciliID(liste[0].gorunumID);
            })
            .catch(() => { setHata(true); setGorunumler([]); });
    }, [arsaId]);

    const secili = gorunumler?.find(g => g.gorunumID === seciliID) ?? null;
    const is3D = secili?.gorunumTipi === '3D';
    const cepheler = useMemo(() => [...(secili?.cepheler ?? [])].sort((a, b) => a.siraNo - b.siraNo), [secili]);

    useEffect(() => { setCepheID(is3D ? (cepheler[0]?.cepheID ?? null) : null); }, [seciliID, is3D, cepheler]);
    // Görünüm / cephe değişince yakınlaştırma ve açık kart sıfırlanır
    useEffect(() => { setGorunum({ z: 1, tx: 0, ty: 0 }); setSabit(null); }, [seciliID, cepheID]);

    const gorsel = is3D ? cepheler.find(c => c.cepheID === cepheID)?.gorselYolu : secili?.arkaplanGorsel;
    const yerlesimler = (secili?.daireler ?? []).filter(d => (is3D ? d.cepheID === cepheID : !d.cepheID));
    const bolumMap = useMemo(() => new Map(bolumler.map(b => [b.bolumID, b])), [bolumler]);

    // İçerik alanı her zaman kaplasın: tx ∈ [W − W·z, 0]
    const sinirla = (z: number, tx: number, ty: number) => {
        const el = alanRef.current;
        if (!el) return { z, tx, ty };
        const w = el.clientWidth, h = el.clientHeight;
        return { z, tx: Math.min(0, Math.max(w - w * z, tx)), ty: Math.min(0, Math.max(h - h * z, ty)) };
    };

    const yakinlastir = (carpan: number, merkez?: { x: number; y: number }) => setGorunum(g => {
        const el = alanRef.current;
        if (!el) return g;
        const nz = Math.min(MAX_ZOOM, Math.max(1, g.z * carpan));
        const cx = merkez?.x ?? el.clientWidth / 2, cy = merkez?.y ?? el.clientHeight / 2;
        // İmlecin altındaki nokta yerinde kalır
        return sinirla(nz, cx - (cx - g.tx) * (nz / g.z), cy - (cy - g.ty) * (nz / g.z));
    });

    // Fare tekerleği — React'in onWheel'i pasif olduğundan sayfa kaymasını engellemek için yerel dinleyici
    useEffect(() => {
        const el = alanRef.current;
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            const r = el.getBoundingClientRect();
            yakinlastir(Math.exp(-e.deltaY * 0.0015), { x: e.clientX - r.left, y: e.clientY - r.top });
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    });

    if (gorunumler === null) {
        return <div className="kka-empty"><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }}></i>Görünümler yükleniyor...</div>;
    }
    if (hata) return <div className="kka-empty">Görünümler yüklenemedi.</div>;
    if (gorunumler.length === 0) {
        return (
            <div className="kka-empty">
                Henüz görünüm eklenmemiş. <b>Bloklar &amp; Katlar → Görünümler</b> bölümünden bir fotoğraf yükleyip bölümleri yerleştirin.
            </div>
        );
    }

    const birakmaOlaylari = (bolumID: number) => {
        const pasif = !surukleniyor || birakilamaz(bolumID);
        return {
            onDragOver: (e: React.DragEvent) => { if (!pasif) { e.preventDefault(); setHedef(bolumID); } },
            onDragLeave: () => setHedef(h => (h === bolumID ? null : h)),
            onDrop: (e: React.DragEvent) => { e.preventDefault(); setHedef(null); if (!pasif) onBirak(bolumID); },
        };
    };

    const chip = (aktif: boolean): React.CSSProperties => ({
        padding: '5px 12px', borderRadius: 20, border: `1.5px solid ${aktif ? '#cd6906' : '#e2e8f0'}`,
        background: aktif ? '#cd6906' : '#fff', color: aktif ? '#fff' : '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    });
    const zoomBtn: React.CSSProperties = {
        width: 30, height: 30, border: 'none', borderRadius: 7, background: 'transparent', color: '#334155', cursor: 'pointer', fontSize: 12,
    };
    const { z, tx, ty } = gorunum;

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                {gorunumler.map(g => (
                    <button key={g.gorunumID} style={chip(g.gorunumID === seciliID)} onClick={() => setSeciliID(g.gorunumID)}>
                        <i className="fas fa-layer-group" style={{ marginRight: 5 }}></i>{g.gorunumAdi}
                        {g.gorunumTipi === '3D' && <span style={{ marginLeft: 5, fontSize: 10, opacity: 0.8 }}>3D</span>}
                    </button>
                ))}
                {is3D && cepheler.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginLeft: 8 }}>
                        {cepheler.map(c => (
                            <button key={c.cepheID} style={{ ...chip(c.cepheID === cepheID), padding: '3px 10px', fontSize: 11 }} onClick={() => setCepheID(c.cepheID)}>
                                {c.cepheAdi}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {!gorsel ? (
                <div className="kka-empty">Bu {is3D ? 'cephenin' : 'görünümün'} fotoğrafı yüklenmemiş.</div>
            ) : (
                <div ref={alanRef}
                    onMouseDown={e => {
                        if (e.button !== 0 || z <= 1) return;
                        kaydirRef.current = { x: e.clientX, y: e.clientY, tx, ty, hareket: false };
                    }}
                    onMouseMove={e => {
                        const k = kaydirRef.current;
                        if (!k) return;
                        if (Math.abs(e.clientX - k.x) + Math.abs(e.clientY - k.y) > 3) k.hareket = true;
                        setGorunum(g => sinirla(g.z, k.tx + e.clientX - k.x, k.ty + e.clientY - k.y));
                    }}
                    onMouseUp={() => { sonSurukleme.current = !!kaydirRef.current?.hareket; kaydirRef.current = null; }}
                    onMouseLeave={() => { kaydirRef.current = null; }}
                    onClick={() => { if (!sonSurukleme.current) setSabit(null); sonSurukleme.current = false; }}
                    style={{
                        position: 'relative', overflow: 'hidden', borderRadius: 10, border: '1px solid #e2e8f0', background: '#0f172a',
                        cursor: z > 1 ? (kaydirRef.current ? 'grabbing' : 'grab') : 'default', userSelect: 'none',
                    }}>
                    <div style={{ transform: `translate(${tx}px, ${ty}px) scale(${z})`, transformOrigin: '0 0' }}>
                        <img src={`${API_BASE_URL}/api/ProjeGorunumleri/gorsel/${gorsel}`} alt="" draggable={false}
                            style={{ display: 'block', width: '100%', height: 'auto' }} />

                        {/* Çizilmiş alanlar — durum renginde, bırakma hedefi */}
                        {yerlesimler.map(y => {
                            const b = bolumMap.get(y.daireID);
                            if (!b || y.rxMin == null || y.ryMin == null || y.rxMax == null || y.ryMax == null) return null;
                            const renk = paylasimRengi(toplamPaylasimOrani(b));
                            const pasif = surukleniyor && birakilamaz(b.bolumID);
                            const vurgu = hedef === b.bolumID || ustteki === b.bolumID || sabit === b.bolumID;
                            return (
                                <div key={`r-${y.daireID}`} {...birakmaOlaylari(b.bolumID)}
                                    onMouseEnter={() => setUstteki(b.bolumID)} onMouseLeave={() => setUstteki(null)}
                                    onClick={e => { e.stopPropagation(); if (!sonSurukleme.current) setSabit(s => (s === b.bolumID ? null : b.bolumID)); sonSurukleme.current = false; }}
                                    style={{
                                        position: 'absolute', left: `${y.rxMin}%`, top: `${y.ryMin}%`,
                                        width: `${y.rxMax - y.rxMin}%`, height: `${y.ryMax - y.ryMin}%`,
                                        background: renk, opacity: pasif ? 0.12 : vurgu ? 0.6 : 0.35,
                                        outline: `${1.5 / z}px solid ${renk}`, cursor: 'pointer',
                                    }} />
                            );
                        })}

                        {/* Bölüm etiketleri — ekranda sabit boyut için yakınlaştırmanın tersiyle ölçeklenir */}
                        {yerlesimler.map(y => {
                            const b = bolumMap.get(y.daireID);
                            if (!b) return null;
                            const oran = toplamPaylasimOrani(b);
                            const pasif = surukleniyor && birakilamaz(b.bolumID);
                            const acik = ustteki === b.bolumID || sabit === b.bolumID;
                            const altta = y.yYuzde < 40;
                            return (
                                <div key={`k-${y.daireID}`} {...birakmaOlaylari(b.bolumID)}
                                    onMouseEnter={() => setUstteki(b.bolumID)} onMouseLeave={() => setUstteki(null)}
                                    onMouseDown={e => e.stopPropagation()}
                                    onClick={e => { e.stopPropagation(); setSabit(s => (s === b.bolumID ? null : b.bolumID)); }}
                                    style={{
                                        position: 'absolute', left: `${y.xYuzde}%`, top: `${y.yYuzde}%`,
                                        transform: `translate(-50%, -50%) scale(${(hedef === b.bolumID ? 1.12 : 1) / z})`,
                                        zIndex: sabit === b.bolumID ? 40 : acik || hedef === b.bolumID ? 30 : 10,
                                        cursor: surukleniyor ? (pasif ? 'not-allowed' : 'copy') : 'pointer',
                                    }}>
                                    <BolumEtiketi ad={b.bolumAdi} oran={oran} vurgulu={acik} hedef={hedef === b.bolumID} pasif={pasif} />
                                    {acik && !surukleniyor && (
                                        <div style={{
                                            position: 'absolute', left: '50%', transform: 'translateX(-50%)',
                                            ...(altta ? { top: '100%', paddingTop: 8 } : { bottom: '100%', paddingBottom: 8 }),
                                        }}>
                                            <BolumDetayKarti
                                                bolum={b}
                                                onKapat={sabit === b.bolumID ? () => setSabit(null) : undefined}
                                                onPaylasimEkle={() => { setSabit(null); onPaylasimEkle(b.bolumID); }}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Yakınlaştırma kontrolleri */}
                    <div onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}
                        style={{ position: 'absolute', right: 10, bottom: 10, zIndex: 60, display: 'flex', alignItems: 'center', gap: 2, padding: 4, background: 'rgba(255,255,255,0.94)', borderRadius: 10, boxShadow: '0 4px 14px rgba(15,23,42,.2)' }}>
                        <button style={zoomBtn} title="Yakınlaştır" onClick={() => yakinlastir(1.5)} disabled={z >= MAX_ZOOM}><i className="fas fa-plus"></i></button>
                        <button style={zoomBtn} title="Uzaklaştır" onClick={() => yakinlastir(1 / 1.5)} disabled={z <= 1}><i className="fas fa-minus"></i></button>
                        <button style={zoomBtn} title="Tümünü göster" onClick={() => setGorunum({ z: 1, tx: 0, ty: 0 })} disabled={z <= 1}><i className="fas fa-expand"></i></button>
                        {z > 1 && <span style={{ fontSize: 10.5, fontWeight: 700, color: '#64748b', padding: '0 6px 0 4px' }}>{Math.round(z * 100)}%</span>}
                    </div>
                </div>
            )}
            <div style={{ marginTop: 8 }}>
                <PaylasimRenkSkalasi ek={`${yerlesimler.length} bölüm · tekerlekle yakınlaştırın · ayrıntı için üzerine gelin ya da tıklayın`} />
            </div>
        </div>
    );
};

export default KatKarsiligGorunumPaylasim;
